import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DocumentIcon } from '@heroicons/react/24/solid';
import ContextMenu from '../ContextMenu';
import {
  IconBox,
  IconRefresh,
  IconUserSolidSquare,
  IconLoadBalancer,
  IconShareAlt,
  IconTrash,
} from '../Icons';
import ContextMenuTriggerButton from '../ContextMenu/ContextMenuTriggerButton';

const ContactActionMenu = ({
  contactId,
  contactUserId,
  canCreateSubcontact,
  canSharePatientContact,
  canReadContactInfos,
  canArchive,
  canDearchive,
  canDelete,
  canDeleteInvitation,
  canResendInvitation,
  canViewContactInfo,
  canStartQuestionnaire,
  onDeleteInvitation,
  onResendInvitation,
  onCreateSubcontact,
  onViewContactInfos,
  onSharePatientContact,
  onArchive,
  onDearchive,
  onDelete,
  onClickOutside,
  onOpen,
  onStartQuestionnaire,
  hidden,
}) => {
  const { t } = useTranslation('components');
  const popupRef = useRef();

  const handleDocumentClick = e => {
    if (popupRef.current) {
      if (!popupRef.current.contains(e.target)) {
        onClickOutside();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  if (!canReadContactInfos) return null;

  return (
    <div data-component="ContactActionMenu" hidden={hidden}>
      <ContextMenu>
        <ContextMenu.Menu closeButtonLabel={t('global:actions.close_button')}>
          <ContextMenu.Group>
            {canDeleteInvitation && (
              <ContextMenu.Item onClick={() => onDeleteInvitation(contactId)}>
                <div className="w-full">
                  {t('PhysicianChatContactList.ContactActionMenu.delete_invitation')}
                </div>
              </ContextMenu.Item>
            )}
            {canResendInvitation && (
              <ContextMenu.Item onClick={() => onResendInvitation(contactId)}>
                <div className="w-full">
                  {t('PhysicianChatContactList.ContactActionMenu.resend_invitation')}
                </div>
              </ContextMenu.Item>
            )}
            {canViewContactInfo && (
              <ContextMenu.Item onClick={() => onViewContactInfos(contactUserId)}>
                <IconUserSolidSquare size="text" />
                <div className="ml-4">
                  {t('PhysicianChatContactList.ContactActionMenu.contact_infos')}
                </div>
              </ContextMenu.Item>
            )}
            {canArchive && (
              <ContextMenu.Item onClick={() => onArchive(contactId)}>
                <IconBox size="text" />
                <div className="ml-4">
                  {t('PhysicianChatContactList.ContactActionMenu.archive')}
                </div>
              </ContextMenu.Item>
            )}
            {canDearchive && (
              <ContextMenu.Item onClick={() => onDearchive(contactId)}>
                <IconRefresh size="text" />
                <div className="ml-4">
                  {t('PhysicianChatContactList.ContactActionMenu.dearchive')}
                </div>
              </ContextMenu.Item>
            )}
            {canDelete && (
              <ContextMenu.Item onClick={() => onDelete(contactId)}>
                <IconTrash size="text" />
                <div className="ml-4">{t('PhysicianChatContactList.ContactActionMenu.delete')}</div>
              </ContextMenu.Item>
            )}
            {canCreateSubcontact && (
              <ContextMenu.Item onClick={() => onCreateSubcontact(contactId)}>
                <IconLoadBalancer size="text" />
                <div className="ml-4">
                  {t('PhysicianChatContactList.ContactActionMenu.create_subcontact')}
                </div>
              </ContextMenu.Item>
            )}
            {canSharePatientContact && (
              <ContextMenu.Item onClick={event => onSharePatientContact(event, contactUserId)}>
                <IconShareAlt size="text" />
                <div className="ml-4">
                  {t('PhysicianChatContactList.ContactActionMenu.share_patient_contact')}
                </div>
              </ContextMenu.Item>
            )}
            {canStartQuestionnaire && (
              <ContextMenu.Item onClick={() => onStartQuestionnaire(contactId)}>
                <DocumentIcon className="w-text h-text" />
                <div className="ml-4">
                  {t('PhysicianChatContactList.ContactActionMenu.startQuestionnaire')}
                </div>
              </ContextMenu.Item>
            )}
          </ContextMenu.Group>
        </ContextMenu.Menu>
        <ContextMenu.Trigger>
          {({ triggerRef }) => (
            <ContextMenuTriggerButton ref={triggerRef} className="lg:flex" onClick={onOpen} />
          )}
        </ContextMenu.Trigger>
      </ContextMenu>
    </div>
  );
};

ContactActionMenu.propTypes = {
  contactId: PropTypes.string,
  contactUserId: PropTypes.string,
  canCreateSubcontact: PropTypes.bool,
  canSharePatientContact: PropTypes.bool,
  canArchive: PropTypes.bool,
  canDearchive: PropTypes.bool,
  canDelete: PropTypes.bool,
  canDeleteInvitation: PropTypes.bool,
  canResendInvitation: PropTypes.bool,
  canStartQuestionnaire: PropTypes.bool,
  onCreateSubcontact: PropTypes.func,
  onViewContactInfos: PropTypes.func,
  onSharePatientContact: PropTypes.func,
  onArchive: PropTypes.func,
  onDearchive: PropTypes.func,
  onDelete: PropTypes.func,
  onClickOutside: PropTypes.func,
  onDeleteInvitation: PropTypes.func,
  onResendInvitation: PropTypes.func,
  canReadContactInfos: PropTypes.bool,
  canViewContactInfo: PropTypes.bool,
  onStartQuestionnaire: PropTypes.func,
  onOpen: PropTypes.func,
  hidden: PropTypes.bool,
};

ContactActionMenu.defaultProps = {
  contactId: null,
  contactUserId: null,
  onCreateSubcontact: () => {},
  onViewContactInfos: () => {},
  onSharePatientContact: () => {},
  onArchive: () => {},
  onDearchive: () => {},
  onClickOutside: () => {},
  onDeleteInvitation: () => {},
  onResendInvitation: () => {},
  onOpen: () => {},
  onDelete: () => {},
  onStartQuestionnaire: () => {},
  hidden: false,
  canReadContactInfos: true,
  canViewContactInfo: true,
  canCreateSubcontact: false,
  canSharePatientContact: false,
  canArchive: false,
  canDearchive: false,
  canDelete: false,
  canDeleteInvitation: false,
  canResendInvitation: false,
  canStartQuestionnaire: false,
};

export default ContactActionMenu;
